import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Loader2,
  FileText,
  ClipboardCheck,
  HardHat,
  Megaphone,
  ArrowRight,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';

const GROUP_ICONS = {
  simulations: FileText,
  pv: ClipboardCheck,
  chantiers: HardHat,
  annonces: Megaphone,
};

/**
 * GlobalCommandPalette — ⌘+K / Ctrl+K cross-collection search overlay.
 *
 * Hits `GET /api/search?q=...` after a 250ms debounce. The result categories
 * (simulations / PV / chantiers / annonces) are rendered as Command groups,
 * each item navigating to the relevant page on click.
 */
export const GlobalCommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const debounceRef = useRef(null);
  const lastQueryRef = useRef('');

  // Global ⌘+K / Ctrl+K shortcut + custom 'open-global-search' event
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const openHandler = () => setOpen(true);
    document.addEventListener('keydown', handler);
    window.addEventListener('open-global-search', openHandler);
    return () => {
      document.removeEventListener('keydown', handler);
      window.removeEventListener('open-global-search', openHandler);
    };
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 2) {
      setGroups([]);
      setLoading(false);
      lastQueryRef.current = '';
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const q = query.trim();
      lastQueryRef.current = q;
      try {
        const resp = await api().get('/search', { params: { q, limit: 6 } });
        // Race protection: only apply if the query is still the latest
        if (lastQueryRef.current === q) {
          setGroups(resp.data?.groups || []);
        }
      } catch (_) {
        if (lastQueryRef.current === q) setGroups([]);
      } finally {
        if (lastQueryRef.current === q) setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, api]);

  const handleSelect = useCallback((url) => {
    setOpen(false);
    setQuery('');
    setGroups([]);
    navigate(url);
  }, [navigate]);

  const totalResults = groups.reduce((sum, g) => sum + (g.items?.length || 0), 0);

  // Don't render the trigger if user isn't authenticated yet
  if (!user) return null;

  return (
    <>
      {/* Discreet trigger button (visible in TopBar) */}
      <button
        onClick={() => setOpen(true)}
        className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors min-w-[220px]"
        data-testid="global-search-trigger"
        type="button"
      >
        <Search className="w-4 h-4" />
        <span className="text-sm">Rechercher…</span>
        <kbd className="ml-auto inline-flex items-center gap-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-1.5 py-0.5 text-[10px] font-mono text-slate-600 dark:text-slate-400">
          ⌘K
        </kbd>
      </button>

      {/* Mobile-only icon trigger */}
      <button
        onClick={() => setOpen(true)}
        className="md:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400"
        data-testid="global-search-trigger-mobile"
        type="button"
        aria-label="Recherche globale"
      >
        <Search className="w-5 h-5" />
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Rechercher sim, PV, chantier, annonce…"
          value={query}
          onValueChange={setQuery}
          data-testid="global-search-input"
        />
        <CommandList data-testid="global-search-list">
          {loading && (
            <div className="py-6 flex items-center justify-center text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Recherche en cours…
            </div>
          )}

          {!loading && query.trim().length < 2 && (
            <div className="py-8 text-center text-sm text-slate-500">
              Tapez au moins <strong>2 caractères</strong> pour rechercher.
              <br />
              <span className="text-xs text-slate-400">
                Astuce : essayez « Leclerc », « Paris », un nom de magasin…
              </span>
            </div>
          )}

          {!loading && query.trim().length >= 2 && totalResults === 0 && (
            <CommandEmpty>Aucun résultat pour « {query} ».</CommandEmpty>
          )}

          {!loading &&
            groups
              .filter((g) => (g.items || []).length > 0)
              .map((g, gi) => {
                const Icon = GROUP_ICONS[g.key] || FileText;
                return (
                  <React.Fragment key={g.key}>
                    {gi > 0 && <CommandSeparator />}
                    <CommandGroup heading={g.label}>
                      {g.items.map((it) => (
                        <CommandItem
                          key={`${g.key}-${it.id}`}
                          value={`${g.key}-${it.id}-${it.title}`}
                          onSelect={() => handleSelect(it.url)}
                          className="flex items-center gap-3 cursor-pointer"
                          data-testid={`global-search-item-${g.key}-${it.id}`}
                        >
                          <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                              {it.title}
                            </div>
                            {it.subtitle && (
                              <div className="text-xs text-slate-500 truncate">
                                {it.subtitle}
                              </div>
                            )}
                          </div>
                          {it.badge && (
                            <Badge variant="outline" className="text-[10px] flex-shrink-0">
                              {it.badge}
                            </Badge>
                          )}
                          <ArrowRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </React.Fragment>
                );
              })}
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default GlobalCommandPalette;
